const INTEGER_PATTERN = /^[+-]?\d+$/

export const MAX_INTEGER_INPUT_LENGTH = 256

export type FractionMode =
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide'
  | 'simplify'
  | 'mixed-to-improper'
  | 'improper-to-mixed'

export type FieldKey = 'firstNumerator' | 'firstDenominator' | 'secondNumerator' | 'secondDenominator' | 'whole'
export type CalculationError =
  | 'invalid_integer'
  | 'integer_too_long'
  | 'zero_denominator'
  | 'divide_by_zero_fraction'
  | 'invalid_mixed_numerator'
  | 'mixed_denominator_not_positive'

export interface Fraction {
  numerator: bigint
  denominator: bigint
}

export interface FractionInputs {
  firstNumerator: string
  firstDenominator: string
  secondNumerator: string
  secondDenominator: string
  whole: string
}

export interface CalculationResult {
  display: string
  fractionDisplay: string
  formula: string
  steps: string[]
}

export interface CalculationState {
  status: 'incomplete' | 'error' | 'ready'
  error: CalculationError | null
  invalidFields: FieldKey[]
  result: CalculationResult | null
  snapshot: string | null
}

function absolute(value: bigint): bigint {
  return value < 0n ? -value : value
}

function greatestCommonDivisor(left: bigint, right: bigint): bigint {
  let a = absolute(left)
  let b = absolute(right)

  while (b !== 0n) {
    const remainder = a % b
    a = b
    b = remainder
  }

  return a
}

export function reduceFraction(numerator: bigint, denominator: bigint): Fraction {
  if (denominator === 0n) throw new Error('Denominator cannot be zero')
  if (numerator === 0n) return { numerator: 0n, denominator: 1n }

  const sign = denominator < 0n ? -1n : 1n
  const normalizedNumerator = numerator * sign
  const normalizedDenominator = denominator * sign
  const divisor = greatestCommonDivisor(normalizedNumerator, normalizedDenominator)

  return {
    numerator: normalizedNumerator / divisor,
    denominator: normalizedDenominator / divisor,
  }
}

function fractionText(fraction: Fraction): string {
  return fraction.denominator === 1n
    ? fraction.numerator.toString()
    : `${fraction.numerator}/${fraction.denominator}`
}

export function mixedNumberText(fraction: Fraction): string {
  const reduced = reduceFraction(fraction.numerator, fraction.denominator)
  const magnitude = absolute(reduced.numerator)
  const whole = magnitude / reduced.denominator
  const remainder = magnitude % reduced.denominator
  const sign = reduced.numerator < 0n ? '-' : ''

  if (remainder === 0n) return `${sign}${whole}`
  if (whole === 0n) return `${sign}${remainder}/${reduced.denominator}`

  return `${sign}${whole} ${remainder}/${reduced.denominator}`
}

export function parseInteger(value: string): bigint | null {
  if (value.length > MAX_INTEGER_INPUT_LENGTH) return null

  const normalized = value.trim()
  if (!INTEGER_PATTERN.test(normalized)) return null

  return BigInt(normalized)
}

function requiredFields(mode: FractionMode): FieldKey[] {
  if (mode === 'add' || mode === 'subtract' || mode === 'multiply' || mode === 'divide') {
    return ['firstNumerator', 'firstDenominator', 'secondNumerator', 'secondDenominator']
  }

  return ['firstNumerator', 'firstDenominator']
}

function normalizedSnapshot(mode: FractionMode, inputs: FractionInputs, values: Partial<Record<FieldKey, bigint>>): string {
  const fields = requiredFields(mode)
  if (mode === 'mixed-to-improper') fields.unshift('whole')

  return [
    mode,
    ...fields.map((field) => {
      const value = values[field]
      const input = inputs[field].trim()

      if (field === 'whole' && value === 0n && input.startsWith('-')) return '-0'

      return value?.toString() ?? input
    }),
  ].join('|')
}

export function calculateFraction(mode: FractionMode, inputs: FractionInputs): CalculationState {
  const required = requiredFields(mode)
  const hasMissingField = required.some((field) => inputs[field].trim() === '')
  const values: Partial<Record<FieldKey, bigint>> = {}
  for (const field of required) {
    if (inputs[field].trim() === '') continue

    if (inputs[field].length > MAX_INTEGER_INPUT_LENGTH) {
      return {
        status: 'error',
        error: 'integer_too_long',
        invalidFields: [field],
        result: null,
        snapshot: normalizedSnapshot(mode, inputs, values),
      }
    }

    const parsed = parseInteger(inputs[field])
    if (parsed === null) {
      return {
        status: 'error',
        error: 'invalid_integer',
        invalidFields: [field],
        result: null,
        snapshot: normalizedSnapshot(mode, inputs, values),
      }
    }
    values[field] = parsed
  }

  if (mode === 'mixed-to-improper' && inputs.whole.trim() !== '') {
    if (inputs.whole.length > MAX_INTEGER_INPUT_LENGTH) {
      return { status: 'error', error: 'integer_too_long', invalidFields: ['whole'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
    }

    const parsedWhole = parseInteger(inputs.whole)
    if (parsedWhole === null) {
      return { status: 'error', error: 'invalid_integer', invalidFields: ['whole'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
    }
    values.whole = parsedWhole
  }

  if (values.firstDenominator === 0n) {
    return { status: 'error', error: 'zero_denominator', invalidFields: ['firstDenominator'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
  }
  if (values.secondDenominator === 0n) {
    return { status: 'error', error: 'zero_denominator', invalidFields: ['secondDenominator'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
  }
  if (mode === 'mixed-to-improper' && values.firstDenominator !== undefined && values.firstDenominator < 0n) {
    return { status: 'error', error: 'mixed_denominator_not_positive', invalidFields: ['firstDenominator'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
  }
  if (hasMissingField) {
    return { status: 'incomplete', error: null, invalidFields: [], result: null, snapshot: null }
  }

  const firstNumerator = values.firstNumerator!
  const firstDenominator = values.firstDenominator!

  if (mode === 'mixed-to-improper') {
    const parsedWhole = values.whole ?? 0n
    values.whole = parsedWhole
    const wholeIsNegative = inputs.whole.trim().startsWith('-')
    const wholeText = wholeIsNegative ? `-${absolute(parsedWhole)}` : parsedWhole.toString()

    const denominator = firstDenominator
    if (firstNumerator < 0n || firstNumerator >= denominator) {
      return { status: 'error', error: 'invalid_mixed_numerator', invalidFields: ['firstNumerator'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
    }

    const improperNumerator = wholeIsNegative
      ? parsedWhole * denominator - firstNumerator
      : parsedWhole * denominator + firstNumerator
    const resultFraction = reduceFraction(improperNumerator, denominator)
    const source = `${wholeText} ${firstNumerator}/${denominator}`

    return {
      status: 'ready',
      error: null,
      invalidFields: [],
      snapshot: normalizedSnapshot(mode, inputs, values),
      result: {
        display: fractionText(resultFraction),
        fractionDisplay: fractionText(resultFraction),
        formula: wholeIsNegative
          ? `(${wholeText} × ${denominator} − ${firstNumerator}) / ${denominator}`
          : `(${wholeText} × ${denominator} + ${firstNumerator}) / ${denominator}`,
        steps: [`Смешанное число: ${source}`, `Неправильная дробь: ${fractionText(resultFraction)}`],
      },
    }
  }

  const first = reduceFraction(firstNumerator, firstDenominator)
  if (mode === 'simplify' || mode === 'improper-to-mixed') {
    const display = mode === 'simplify' ? fractionText(first) : mixedNumberText(first)
    return {
      status: 'ready',
      error: null,
      invalidFields: [],
      snapshot: normalizedSnapshot(mode, inputs, values),
      result: {
        display,
        fractionDisplay: fractionText(first),
        formula: mode === 'simplify'
          ? `${firstNumerator}/${firstDenominator} → ${fractionText(first)}`
          : `${fractionText(first)} → ${mixedNumberText(first)}`,
        steps: mode === 'simplify'
          ? [`НОД: ${greatestCommonDivisor(firstNumerator, firstDenominator)}`, 'Знак оставлен в числителе, знаменатель положительный']
          : ['Целая часть и остаток найдены по модулю числителя', `Сокращённая дробь: ${fractionText(first)}`],
      },
    }
  }

  const secondNumerator = values.secondNumerator!
  const secondDenominator = values.secondDenominator!
  if (mode === 'divide' && secondNumerator === 0n) {
    return { status: 'error', error: 'divide_by_zero_fraction', invalidFields: ['secondNumerator'], result: null, snapshot: normalizedSnapshot(mode, inputs, values) }
  }

  const second = reduceFraction(secondNumerator, secondDenominator)
  const operator = mode === 'add' ? '+' : mode === 'subtract' ? '−' : mode === 'multiply' ? '×' : '÷'
  let rawNumerator: bigint
  let rawDenominator: bigint

  if (mode === 'add' || mode === 'subtract') {
    const signedSecond = mode === 'add' ? second.numerator : -second.numerator
    rawNumerator = first.numerator * second.denominator + signedSecond * first.denominator
    rawDenominator = first.denominator * second.denominator
  } else if (mode === 'multiply') {
    rawNumerator = first.numerator * second.numerator
    rawDenominator = first.denominator * second.denominator
  } else {
    rawNumerator = first.numerator * second.denominator
    rawDenominator = first.denominator * second.numerator
  }

  const normalizedRawNumerator = rawDenominator < 0n ? -rawNumerator : rawNumerator
  const normalizedRawDenominator = absolute(rawDenominator)
  const resultFraction = reduceFraction(normalizedRawNumerator, normalizedRawDenominator)
  const formula = `${fractionText(first)} ${operator} ${fractionText(second)}`

  return {
    status: 'ready',
    error: null,
    invalidFields: [],
    snapshot: normalizedSnapshot(mode, inputs, values),
    result: {
      display: fractionText(resultFraction),
      fractionDisplay: fractionText(resultFraction),
      formula,
      steps: [
        `${formula} = ${normalizedRawNumerator}/${normalizedRawDenominator}`,
        `Сокращённый результат: ${fractionText(resultFraction)}`,
      ],
    },
  }
}
